import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ToDoTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState('');

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load tasks');
      setLoading(false);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) {
      toast.error('Please enter a task description');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tasks`, {
        description: newTask,
        property_id: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Task added');
      setNewTask('');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tasks/${taskId}?completed=${!completed}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  if (loading) {
    return <div className="tab-loading">Loading tasks...</div>;
  }

  const incompleteTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  return (
    <div className="todo-tab">
      <form onSubmit={handleAddTask} className="add-task-form">
        <Input
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          placeholder="Add a new task..."
          className="task-input"
          data-testid="new-task-input"
        />
        <Button type="submit" className="btn-add-task" data-testid="add-task-button">
          <Plus size={20} />
          Add
        </Button>
      </form>

      {incompleteTasks.length === 0 && completedTasks.length === 0 ? (
        <div className="empty-state" data-testid="empty-tasks">
          <CheckCircle2 size={64} className="empty-icon" />
          <h3>No tasks yet</h3>
          <p>Add tasks to keep track of your work. Renewal reminders live on the Renewals page.</p>
        </div>
      ) : (
        <div className="tasks-container">
          {incompleteTasks.length > 0 && (
            <div className="tasks-section">
              <h3 className="section-title">To Do</h3>
              <div className="tasks-list">
                {incompleteTasks.map((task) => (
                  <div key={task.id} className="task-item" data-testid={`task-${task.id}`}>
                    <Checkbox
                      checked={task.completed}
                      onCheckedChange={() => handleToggleTask(task.id, task.completed)}
                      data-testid={`task-checkbox-${task.id}`}
                    />
                    <div className="task-content">
                      <div className="task-description" data-testid="task-description">{task.description}</div>
                    </div>
                    <Button
                      onClick={() => handleDeleteTask(task.id)}
                      className="btn-delete-task"
                      data-testid={`delete-task-${task.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {completedTasks.length > 0 && (
            <div className="tasks-section">
              <h3 className="section-title">Completed</h3>
              <div className="tasks-list">
                {completedTasks.map((task) => (
                  <div key={task.id} className="task-item task-completed" data-testid={`task-completed-${task.id}`}>
                    <Checkbox
                      checked={task.completed}
                      onCheckedChange={() => handleToggleTask(task.id, task.completed)}
                      data-testid={`task-checkbox-completed-${task.id}`}
                    />
                    <div className="task-content">
                      <div className="task-description completed">{task.description}</div>
                    </div>
                    <Button
                      onClick={() => handleDeleteTask(task.id)}
                      className="btn-delete-task"
                      data-testid={`delete-task-completed-${task.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
